import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";
import api from "services/api";

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState("pending"); // pending, loading, success, error
  const [isResending, setIsResending] = useState(false);

  const verifyEmail = async () => {
    setVerificationStatus("loading");
    try {
      console.log(token, email);
      const response = await api.post(`/auth/verify-email`, { token, email });
      
      if (!response.ok) {
        setVerificationStatus("error");
        toast.error(response.message || "Verification failed");
        return;
      }

      setVerificationStatus("success");
      toast.success("Email verified successfully!");
      navigate("/login"); 
      
    } catch (error) {
      setVerificationStatus("error");
      toast.error(error.message || "An error occurred during verification");
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const response = await api.post("/auth/resend-verification", { email });
      
      if (!response.ok) {
        toast.error(response.message || "Failed to send verification email");
        return;
      } 

      toast.success(response.message || "Verification email sent successfully!");

    } catch (error) {
      toast.error(error.message || "An error occurred while sending verification email");
    } finally {
      setIsResending(false);
    }
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case "loading":
        return <FaSpinner className="animate-spin text-black" />;
      case "success":
        return <FaCheckCircle className="text-green-500" />;
      case "error":
        return <FaTimesCircle className="text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (verificationStatus) {
      case "pending":
        return "Ready to verify";
      case "loading":
        return "Verifying your email...";
      case "success":
        return "Email verified successfully!";
      case "error":
        return "Verification failed";
      default:
        return "";
    }
  };

  const getStatusDescription = () => {
    switch (verificationStatus) {
      case "pending":
        return "Click the button below to verify your email address.";
      case "loading":
        return "Please wait while we verify your email address.";
      case "success":
        return "Your email has been verified successfully. You can now access all features of RigidAthlete.";
      case "error":
        return "There was a problem verifying your email. Please try again or contact support.";
      default:
        return "";
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
            Email verification
          </h1>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="text-6xl">
                {getStatusIcon()}
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {getStatusText()}
            </h2>
            
            <p className="text-gray-600 mb-6">
              {getStatusDescription()}
            </p>

            {verificationStatus === "pending" && (
              <button
                onClick={verifyEmail}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Verify email
              </button>
            )}

            {verificationStatus === "success" && (
              <div className="space-y-4">
                <button
                  onClick={() => navigate("/home")}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Go to dashboard
                </button>
                <Link
                  to="/login"
                  className="block text-sm text-black hover:text-gray-700"
                >
                  Or login to your account
                </Link>
              </div>
            )}

            {verificationStatus === "error" && (
              <div className="space-y-4">
                <button
                  onClick={verifyEmail}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Try again
                </button>
                <button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? "Sending..." : "Resend verification email"}
                </button>
                <Link
                  to="/login"
                  className="block text-sm text-black hover:text-gray-700"
                >
                  Back to login
                </Link>
                <Link
                  to="/register"
                  className="block text-sm text-gray-600 hover:text-gray-500"
                >
                  Create a new account
                </Link>
              </div>
            )}

            {verificationStatus === "loading" && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  This may take a few moments...
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
